import { makeStyles } from '@material-ui/core/styles'
import type { Theme } from '@material-ui/core'
import { getPresentationContentStyles } from '@paintscout/ui/styles'
import type { EditorProps } from './Editor'
import { templatePlaceholderLineHeight, templatePlaceholderStyles } from './templatePlaceholderStyles'
import { isInlineEditor, trailingBreakStyles } from './trailingBreakStyles'
import { blankLineStyles } from './blankLineStyles'

/** Content font size for `variant="compact"`, from the prototype's 14px terms body. */
const COMPACT_FONT = 14

export const useEditorStyles = makeStyles<Theme, EditorProps>((theme) => {
  return {
    editorContent: (props) => ({
      cursor: props.disabled ? 'default' : 'text',
      flexGrow: '2'
    }),
    root: (props) => {
      const blankLine = blankLineStyles(theme, isInlineEditor(props))
      const compact = props.variant === 'compact'

      return {
        ...(props.label && { marginTop: theme.spacing(1) }),
        ...getPresentationContentStyles(theme, { noMargins: ['li'] }),
        ...props.contentStyles,
        borderRadius: theme.borderRadius.md,
        ...(props.showFileBar &&
          (props.files?.length > 0 || props.content.length > 0) && {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0
          }),
        position: 'relative',
        width: '100%',
        border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        '& input': {
          fontFamily: theme.typography.fontFamily,
          fontSize: theme.typography.pxToRem(16),
          '&:focus-visible': {
            outline: 'none !important'
          }
        },
        backgroundColor: isInlineEditor(props)
          ? 'transparent'
          : props.disabled
            ? theme.paintscout.palette.brand.Neutral.P94
            : theme.palette.background.paper,
        minHeight: 120,
        ...(props.singleLine && {
          minHeight: 0
        }),
        '&:hover:not(:focus-within)': {
          borderColor: isInlineEditor(props) ? 'none' : !props.disabled && 'transparent',
          boxShadow: isInlineEditor(props)
            ? 'none'
            : props.disabled
              ? 'none'
              : `0 0 0 2px ${theme.paintscout.palette.brand.Neutral.P70}`,
          boxSizing: 'border-box'
        },
        '&:focus-within': {
          borderColor: 'transparent',
          boxShadow: isInlineEditor(props) ? 'none' : `0 0 0 2px ${theme.paintscout.palette.primary}`
        },

        '& .ProseMirror': (props) => ({
          opacity: props.disabled ? 0.5 : 1,
          outline: 'none',
          padding: isInlineEditor(props) ? 0 : theme.size['2'],
          fontSize: theme.typography.fontSize,
          display: 'block',
          flexGrow: 1,
          maxWidth: '100%',
          overflowWrap: 'break-word',
          '&:hover': {},
          // Hide tiptap's trailing-break `<br>` hack, except on a genuinely empty paragraph, where
          // it is the only thing giving the blank line a caret position (#21916, #22366). Showing
          // it anywhere else renders a phantom trailing line (#22034). See trailingBreakStyles.
          ...trailingBreakStyles(),
          '& .ps-persistent-selection': {
            backgroundColor: 'rgba(155, 195, 255, 0.5)'
          },
          // Empty-editor hint text from the `placeholder` prop (see EmptyPlaceholder). float + height 0
          // keeps it out of the layout so the caret still sits at the start of the line.
          '& .is-editor-empty::before': {
            content: 'attr(data-placeholder)',
            color: theme.paintscout.palette.brand.Neutral.P60,
            float: 'left',
            height: 0,
            pointerEvents: 'none'
          }
        }),
        '& .ProseMirror p': {
          whiteSpace: 'break-spaces',
          // Keep a blank line exactly one line tall, matching the email. See blankLineStyles.
          ...blankLine.paragraph,
          // Slight increase in line height when placeholders are present to prevent border overlap
          '&:has(.template-placeholder)': templatePlaceholderLineHeight
        },
        '& .ProseMirror p::after': blankLine.after,
        '& .template-placeholder': templatePlaceholderStyles(theme),
        // this and all the flexGrow stuff is so the ProseMirror (clickable/editable area) is the full height of root (adjustable by props)
        // hacky solution as there is an aria inserted div by tiptap that needs to be set to flex/flexGrow 1 but can't be targeted
        '& div': {
          display: 'flex'
          // flexGrow: 1
        },
        '& #menu-bar': {
          position: 'sticky',
          // top: 0 keeps the whole toolbar visible when stuck — a negative offset
          // clipped the top, hiding the first row when the toolbar wraps on narrow screens (#17775)
          top: 0,
          zIndex: 2,
          borderRadius: `${theme.borderRadius.md} ${theme.borderRadius.md} 0 0`,
          boxShadow: 'none',
          borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
          ...(props.error && {
            backgroundColor: theme.paintscout.palette.semantic.Danger.S90,
            borderBottom: `1px solid ${theme.paintscout.palette.semantic.Danger.S60}`
          }),
          ...(props.disabled && {
            backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
            borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P70}`
          })
        },
        // `variant="compact"` shrinks the content text and nothing else.
        //
        // These use `&&` rather than `&`, for two reasons. It is a different key from the `& .ProseMirror`
        // rule above, so it adds to that rule instead of replacing it (a second `& .ProseMirror` key in
        // this object literal would drop the padding, placeholder and trailing-break styles). And the
        // doubled specificity outranks `getPresentationContentStyles`, which sizes paragraphs itself.
        ...(compact && {
          '&& .ProseMirror': {
            fontSize: theme.typography.pxToRem(COMPACT_FONT)
          },
          '&& .ProseMirror p, && .ProseMirror li': {
            fontSize: theme.typography.pxToRem(COMPACT_FONT)
          }
        }),
        ...(props.error && {
          border: `1px solid ${theme.paintscout.palette.semantic.Danger.S60}`,
          backgroundColor: theme.paintscout.palette.semantic.Danger.S90
        })
      }
    },
    dropRoot: (props) => ({
      // Hover effect when dropping
      borderColor: 'transparent',
      boxShadow: isInlineEditor(props) ? 'none' : `0 0 0 2px ${theme.paintscout.palette.primary}`,
      // Hide odd lines that appear when dropping
      '& .prosemirror-dropcursor-block': {
        display: 'none'
      }
    }),
    addRounding: {
      borderRadius: theme.borderRadius.md
    },
    hideBottomRounding: {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0
    },
    withFileBar: () => ({
      minHeight: 168
    }),
    noDisplay: {
      '&$noDisplay': {
        display: 'none'
      }
    },
    fileDivContainer: {
      borderRadius: theme.borderRadius.md,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      width: '100%',
      borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
      display: 'flex',
      padding: theme.spacing(0.5)
    },
    filesContainer: {
      width: '100%',
      display: 'flex',
      overflowX: 'auto',
      alignSelf: 'flex-end'
    },
    fileItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(0.75),
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
      marginRight: theme.spacing(1),
      borderRadius: theme.borderRadius.md,
      whiteSpace: 'nowrap'
    },
    fileItemText: {
      '&:hover': {
        color: theme.paintscout.palette.brand.Neutral.P60,
        cursor: 'pointer'
      }
    },
    fileCloseIcon: {
      color: theme.paintscout.palette.brand.Neutral.P80,
      marginLeft: theme.spacing(1),
      '&:hover': {
        color: theme.paintscout.palette.brand.Neutral.P60,
        cursor: 'pointer'
      }
    },
    characterCountDiv: {
      marginTop: theme.spacing(0.5)
    },
    errorDiv: (props) => ({
      marginTop: props.characterCount ? theme.spacing(0) : theme.spacing(0.5)
    })
  }
})
